import { useQuery } from '@tanstack/react-query';
import { hoursToMilliseconds } from 'date-fns';
import { search as fuzzySearch } from 'fast-fuzzy';
import { useUrlManager } from 'hooks/use-url-manager/use-url-manager';
import { listProjects, ProjectListResponse, ProjectResponse } from 'sync/project-service';
import { PerPageValue, QueryOptions } from 'sync/query';
import { SearchResult } from 'types/store';
import { ReactQueryKeys } from 'utils/react-query';
import { matchLeftCharacters } from 'utils/strings';
import { getEntireProjectList, getSearchKeySelectors } from './use-project-list.utils';

type UseProjectListProps = {
  organizationId: string;
  refetchInterval?: number | false;
  enabled?: boolean;
  sortBy?: QueryOptions['sortBy'];
  sortDirection?: QueryOptions['sortDirection'];
  onSuccess?: (data: ProjectListResponse | null) => void;
} & (
  | {
      page?: number;
      perPage?: PerPageValue;
    }
  | {
      entire: true;
    }
);

export const DEFAULT_PAGE = 1;
export const DEFAULT_PER_PAGE = 100;
const PROJECT_LIST_CACHE_TIME = hoursToMilliseconds(10);

export const useProjectList = ({
  organizationId,
  refetchInterval = false,
  sortBy,
  sortDirection,
  enabled = true,
  onSuccess,
  ...props
}: UseProjectListProps) => {
  const { createURL } = useUrlManager();

  const page = 'page' in props ? props.page || DEFAULT_PAGE : DEFAULT_PAGE;
  const perPage = 'perPage' in props ? props.perPage || DEFAULT_PER_PAGE : DEFAULT_PER_PAGE;
  const entire = 'entire' in props ? props.entire : false;

  const query = useQuery(
    ReactQueryKeys.PROJECT_LIST({
      organizationId,
      page,
      perPage,
      entire,
      sortBy,
      sortDirection,
    }),
    async () => {
      if (entire) {
        return getEntireProjectList({ organizationId, startPage: page, perPage });
      }

      return listProjects({ organizationId }, { page, perPage, sortBy, sortDirection });
    },
    { cacheTime: PROJECT_LIST_CACHE_TIME, refetchInterval, enabled: !!organizationId && enabled, onSuccess }
  );

  const projectList = query.data?.data.filter((record) => record.data) || [];

  /**
   * `search` performs a custom search against the in-memory list of
   * Projects.
   * */
  const search = (term: string, maxResults?: number): SearchResult[] => {
    const results: SearchResult[] = [];

    if (!term || projectList.length === 0) {
      return results;
    }

    const lowercaseTerm = term.toLowerCase();
    const set = new Set();

    const addUniqueResult = (record: ProjectResponse, rank: number) => {
      // Quit if we have reached a desired maximum result size.
      if (maxResults && set.size >= maxResults) {
        return;
      }

      // Because we can search multiple properties we can have duplicates.
      // Deduplicate by ID;
      const { id } = record.data;

      if (set.has(id)) {
        return;
      }

      results.push({
        href: createURL('/project/databases', {
          organizationId: record.data.tenantId,
          projectId: record.data.id,
        }),
        icon: 'projects',
        rank,
        resource: 'project',
        title: record.data.name,
        summary: 'Project',
        isFocused: false,
      });

      set.add(id);
    };

    // First, let's check to see if someone is searching for an exact match.
    projectList.forEach((record: ProjectResponse) => {
      // If the search term looks like they just want to list projects, then give them.
      if (matchLeftCharacters(term, ['projects'])) {
        addUniqueResult(record, 1);
        return;
      }

      // Check the ID?
      if (record.data.id.toLowerCase() === lowercaseTerm) {
        addUniqueResult(record, 100);
      }
    });

    // If our set is populated we've found an exact match on an ID and we can stop looking. There won't be another GUID like this one!
    if (set.size !== 0) {
      return results.sort((a, b) => {
        return a.title.localeCompare(b.title);
      });
    }

    // Finally, we will iterate over the fields we wish to search and collect matches.
    getSearchKeySelectors().forEach((keySelector, index) => {
      // We may not need to perform further, less relevant searches...
      if (maxResults && set.size >= maxResults) {
        return;
      }

      const matches = fuzzySearch(lowercaseTerm, projectList, {
        returnMatchData: true,
        keySelector: keySelector.fn,
      });

      matches.forEach(({ item, score }) => {
        addUniqueResult(item, score - index);
      });
    });

    // Return a sorted list of results with the highest rank first.
    return results.sort((a, b) => {
      return b.rank - a.rank;
    });
  };

  return { search, projectList, query, entireListReady: query.data && query.data?.data.length === query.data?.cursor.pages.totalItems };
};
